/**
 * AccountPage
 * =============================================================================
 * Shell tab at /account: read-only profile, saved searches, watched labels
 * (US label subscriptions) and starred lists. Replaces the old subscriptions page.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/authApi';
import type { SubscriptionResponse } from '../services/authApi';
import { StarredListsBody } from './StarredListsPage';

type SectionId = 'profile' | 'saved' | 'watched' | 'starred';

const getInitials = (name: string | null, email: string | null): string => {
  if (name) {
    const parts = name.split(' ');
    if (parts.length >= 2 && parts[0] && parts[1]) {
      const firstChar = parts[0][0];
      const secondChar = parts[1][0];
      if (firstChar && secondChar) {
        return (firstChar + secondChar).toUpperCase();
      }
    }
    return name.substring(0, 2).toUpperCase();
  }
  if (email) {
    return email.substring(0, 2).toUpperCase();
  }
  return 'U';
};

const fmt = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const NAV_ITEMS: { id: SectionId; label: string }[] = [
  { id: 'profile', label: 'Profile' },
  { id: 'saved', label: 'Saved searches' },
  { id: 'watched', label: 'Watched labels' },
  { id: 'starred', label: 'Starred lists' },
];

// Columns switch on by the list's own width (container query), not the viewport:
// the shell's resizable chat panel decides how wide this page actually is.
// Threshold = fixed tracks + gaps + room for the name.
const SEARCH_GRID = '[@container_(min-width:680px)]:grid [@container_(min-width:680px)]:grid-cols-[minmax(0,1fr)_110px_120px_80px_150px]';
const LABEL_GRID = '[@container_(min-width:540px)]:grid [@container_(min-width:540px)]:grid-cols-[minmax(0,1fr)_120px_80px_150px]';

interface ListProps {
  kind: 'search' | 'label';
  rows: SubscriptionResponse[];
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  busy: ReadonlySet<number>;
  confirmId: number | null;
  onToggle: (s: SubscriptionResponse) => void;
  onAskDelete: (id: number) => void;
  onCancelDelete: () => void;
  onConfirmDelete: (id: number) => void;
  empty: string;
}

const SubscriptionList: React.FC<ListProps> = ({
  kind, rows, loading, failed, onRetry, busy, confirmId,
  onToggle, onAskDelete, onCancelDelete, onConfirmDelete, empty,
}) => {
  if (loading) return <p role="status" className="mt-6 text-sm text-ink-3">Loading…</p>;
  if (failed) {
    return (
      <div className="mt-6 flex flex-wrap items-center gap-3 text-sm">
        <span>Couldn't load this list.</span>
        <button type="button" className="btn-secondary" onClick={onRetry}>Retry</button>
      </div>
    );
  }
  if (rows.length === 0) return <p className="mt-6 text-sm text-ink-3">{empty}</p>;

  const grid = kind === 'search' ? SEARCH_GRID : LABEL_GRID;
  return (
    <div className="mt-6 [container-type:inline-size]">
      <div className={`hidden ${grid} gap-x-4 border-b border-line pb-2 text-xs text-ink-3`}>
        <span>{kind === 'search' ? 'Search' : 'Label'}</span>
        {kind === 'search' && <span>Scope</span>}
        <span>Last alert</span>
        <span>Alerts</span>
        <span className="sr-only">Actions</span>
      </div>
      <ul>
        {rows.map((row) => {
          const pending = busy.has(row.id);
          return (
            <li
              key={row.id}
              className={`flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-line-subtle py-3 text-sm ${grid}`}
            >
              <div className="flex min-w-0 items-center gap-2">
                {kind === 'search' && (
                  <span className="eyebrow rounded-ctl bg-accent-tint px-1.5 py-0.5">KEYWORD</span>
                )}
                <span className="truncate" title={row.name}>{row.name}</span>
              </div>
              {kind === 'search' && <span className="text-ink-2">Labels</span>}
              <span className="text-ink-2">{fmt(row.last_notified_at)}</span>
              <label className="inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={row.is_active}
                  disabled={pending}
                  onChange={() => onToggle(row)}
                  aria-label={`Alerts for ${row.name}`}
                  className="accent-accent"
                />
                {row.is_active ? 'On' : 'Off'}
              </label>
              <div className="flex items-center gap-2">
                {confirmId === row.id ? (
                  <>
                    <span className="text-ink-2">Delete?</span>
                    <button
                      type="button"
                      aria-label="Confirm delete"
                      disabled={pending}
                      onClick={() => onConfirmDelete(row.id)}
                      className="inline-flex h-8 items-center rounded-ctl bg-red-600 px-3 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      aria-label="Cancel delete"
                      onClick={onCancelDelete}
                      className="btn-secondary h-8 px-3"
                    >
                      No
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    aria-label={`Delete ${row.name}`}
                    disabled={pending}
                    onClick={() => onAskDelete(row.id)}
                    className="btn-secondary h-8 px-3"
                  >
                    Delete
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const AccountPage: React.FC = () => {
  const { user } = useAuth();
  const [subs, setSubs] = useState<SubscriptionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<ReadonlySet<number>>(new Set());
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; error: boolean } | null>(null);

  const mounted = useRef(true);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sectionRefs: Record<SectionId, React.RefObject<HTMLElement>> = {
    profile: useRef<HTMLElement>(null),
    saved: useRef<HTMLElement>(null),
    watched: useRef<HTMLElement>(null),
    starred: useRef<HTMLElement>(null),
  };
  const headingRefs: Record<SectionId, React.RefObject<HTMLHeadingElement>> = {
    profile: useRef<HTMLHeadingElement>(null),
    saved: useRef<HTMLHeadingElement>(null),
    watched: useRef<HTMLHeadingElement>(null),
    starred: useRef<HTMLHeadingElement>(null),
  };

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = useCallback((message: string, error: boolean) => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, error });
    toastTimer.current = setTimeout(() => setToast(null), 3500);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const data = await authApi.listSubscriptions();
      if (!mounted.current) return;
      setSubs(data);
    } catch {
      if (!mounted.current) return;
      setFailed(true);
    }
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const setPending = (id: number, on: boolean) =>
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(id); else next.delete(id);
      return next;
    });

  const toggle = async (sub: SubscriptionResponse) => {
    setPending(sub.id, true);
    try {
      const updated = await authApi.patchSubscription(sub.id, { is_active: !sub.is_active });
      if (!mounted.current) return;
      setSubs((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      showToast(updated.is_active ? 'Alerts on.' : 'Alerts off.', false);
    } catch {
      if (mounted.current) showToast("Couldn't update alerts. Try again.", true);
    } finally {
      if (mounted.current) setPending(sub.id, false);
    }
  };

  const remove = async (id: number) => {
    setPending(id, true);
    try {
      await authApi.deleteSubscription(id);
      if (!mounted.current) return;
      setSubs((prev) => prev.filter((s) => s.id !== id));
      showToast('Deleted.', false);
    } catch {
      if (mounted.current) showToast("Couldn't delete. Try again.", true);
    } finally {
      if (mounted.current) {
        setPending(id, false);
        setConfirmId(null);
      }
    }
  };

  const goTo = (id: SectionId) => {
    sectionRefs[id].current?.scrollIntoView({ block: 'start' });
    headingRefs[id].current?.focus({ preventScroll: true });
  };

  const email = user?.email ?? null;
  const displayName = user?.displayName || email?.split('@')[0] || 'User';
  const initials = getInitials(user?.displayName ?? null, email);
  const photoURL = user?.photoURL;

  const listProps = {
    loading, failed, onRetry: load, busy, confirmId,
    onToggle: toggle,
    onAskDelete: setConfirmId,
    onCancelDelete: () => setConfirmId(null),
    onConfirmDelete: remove,
  };
  const searches = subs.filter((s) => s.type !== 'label');
  const labels = subs.filter((s) => s.type === 'label');

  const headingCls = 'font-display focus:outline-none';
  const sectionCls = 'mt-10 border-t border-line pt-10';

  return (
    <div className="min-h-full bg-ground text-ink">
      <div className="mx-auto max-w-[1040px] px-4 py-8 md:flex md:gap-10">
        <nav
          aria-label="Account sections"
          className="mb-8 md:mb-0 md:w-[220px] md:shrink-0 md:self-start md:sticky md:top-0"
        >
          <p className="eyebrow mb-2">Your account</p>
          {NAV_ITEMS.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              onClick={(e) => { e.preventDefault(); goTo(id); }}
              className="flex min-h-10 items-center rounded-ctl px-3 text-ink-2 hover:bg-accent-tint hover:text-accent-deep focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-accent-ring"
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="min-w-0 flex-1 md:max-w-[760px]">
          <section id="profile" ref={sectionRefs.profile} aria-labelledby="account-profile-h">
            <h1 id="account-profile-h" ref={headingRefs.profile} tabIndex={-1} className={`${headingCls} text-[30px]`}>
              Profile
            </h1>
            <div className="mt-6 flex flex-wrap items-center gap-6">
              <div className="flex h-[88px] w-[88px] items-center justify-center overflow-hidden rounded-full bg-ink text-ground text-2xl font-medium">
                {photoURL ? (
                  <img src={photoURL} alt="" className="h-full w-full rounded-full object-cover" />
                ) : (
                  initials
                )}
              </div>
              <dl className="min-w-0 space-y-4 text-sm">
                <div>
                  <dt className="text-xs text-ink-3">Name</dt>
                  <dd className="mt-1">{displayName}</dd>
                </div>
                <div>
                  <dt className="text-xs text-ink-3">Email</dt>
                  <dd className="mt-1 break-all">{email ?? ''}</dd>
                  <dd className="mt-1 text-xs text-ink-3">Alerts are sent here. Contact your admin to change it.</dd>
                </div>
              </dl>
            </div>
          </section>

          <section id="saved" ref={sectionRefs.saved} aria-labelledby="account-saved-h" className={sectionCls}>
            <h2 id="account-saved-h" ref={headingRefs.saved} tabIndex={-1} className={`${headingCls} text-[24px]`}>
              Saved searches
            </h2>
            <p className="mt-2 text-sm text-ink-2">
              Save a search from any Keyword results page with "Save search". You're alerted when a new or updated label matches it.
            </p>
            <SubscriptionList kind="search" rows={searches} empty="No saved searches yet." {...listProps} />
          </section>

          <section id="watched" ref={sectionRefs.watched} aria-labelledby="account-watched-h" className={sectionCls}>
            <h2 id="account-watched-h" ref={headingRefs.watched} tabIndex={-1} className={`${headingCls} text-[24px]`}>
              Watched labels
            </h2>
            <p className="mt-2 text-sm text-ink-2">
              Labels you get alerts for. Turn alerts on with "Watch for changes" on a label page. Changes are detected when each source is refreshed.
            </p>
            <SubscriptionList kind="label" rows={labels} empty="No watched labels yet." {...listProps} />
          </section>

          <section id="starred" ref={sectionRefs.starred} aria-labelledby="account-starred-h" className={sectionCls}>
            <h2 id="account-starred-h" ref={headingRefs.starred} tabIndex={-1} className={`${headingCls} text-[24px]`}>
              Starred lists
            </h2>
            <p className="mt-2 text-sm text-ink-2">
              Star drugs in Compare, then "Save Starred List". Open a list to reopen those drugs in Compare.
            </p>
            <StarredListsBody />
          </section>
        </div>
      </div>

      {toast && (
        <div
          role="status"
          className={`fixed bottom-4 right-4 z-50 rounded-ctl px-4 py-3 text-sm shadow ${
            toast.error ? 'bg-red-600 text-white' : 'bg-ink text-ground'
          }`}
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default AccountPage;
